import React from 'react';
import PropTypes from 'prop-types';
import MediaAttachment from './MediaAttachment';

const DEFAULT_BUSY_STATUSES = new Set(['preparing', 'uploading', 'awaiting_upload', 'scanning']);

function defaultStatusDetail(status, progress) {
  if (status === 'uploading') return `${progress}%`;
  if (status === 'preparing') return 'Preparing';
  if (status === 'error') return 'Failed';
  return 'Scanning';
}

function attachmentSummary(items = [], busyStatuses = DEFAULT_BUSY_STATUSES) {
  const total = items.length;
  const busyCount = items.filter((item) => busyStatuses.has(item.status)).length;
  const errorCount = items.filter((item) => item.status === 'error').length;
  if (busyCount > 0) return `Uploading ${busyCount} of ${total}`;
  if (errorCount > 0) return `${errorCount} failed, ${total} ${total === 1 ? 'attachment' : 'attachments'}`;
  return `${total} ${total === 1 ? 'attachment' : 'attachments'}`;
}

export default function ComposerMediaRail({
  items = [],
  onRemove,
  removeButtonClassName = '',
  busyStatuses = DEFAULT_BUSY_STATUSES,
  statusDetail = defaultStatusDetail,
}) {
  if (!items.length) return null;

  return (
    <div className="composer-media-rail" aria-label="Selected media attachments">
      <div className="composer-media-rail__summary">{attachmentSummary(items, busyStatuses)}</div>
      <div className="composer-media-rail__scroller">
        {items.map((item) => {
          const isBusy = busyStatuses.has(item.status);
          const isError = item.status === 'error';
          const statusText = item.error || statusDetail(item.status, item.progress);
          const progress = item.status === 'uploading' ? Math.max(4, item.progress || 0) : 100;
          return (
            <div
              key={item.id}
              className={`composer-media-rail__tile composer-media-rail__tile--${item.status || 'pending'}`}
            >
              {item.media ? (
                <MediaAttachment media={item.media} className="composer-media-rail__preview" compact />
              ) : (
                <div className="composer-media-rail__placeholder" aria-hidden="true">
                  <span>{item.fileName || 'Media'}</span>
                </div>
              )}
              {(isBusy || isError || !item.media) && (
                <div className="composer-media-rail__overlay">
                  <span>{statusText}</span>
                  {!isError && (
                    <span className="composer-media-rail__progress" aria-hidden="true">
                      <span style={{ width: `${progress}%` }} />
                    </span>
                  )}
                </div>
              )}
              <button
                type="button"
                className={['composer-media-rail__remove', removeButtonClassName].filter(Boolean).join(' ')}
                onClick={() => onRemove(item.id)}
                disabled={isBusy}
                aria-label={`Remove ${item.fileName || 'selected media'}`}
                title="Remove selected media"
              >
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const mediaShape = PropTypes.shape({
  provider: PropTypes.string,
  uploadId: PropTypes.string,
  mediaType: PropTypes.string,
  storagePath: PropTypes.string,
  thumbnailPath: PropTypes.string,
  fileName: PropTypes.string,
});

ComposerMediaRail.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    status: PropTypes.string,
    progress: PropTypes.number,
    error: PropTypes.string,
    media: mediaShape,
  })),
  onRemove: PropTypes.func.isRequired,
  removeButtonClassName: PropTypes.string,
  busyStatuses: PropTypes.instanceOf(Set),
  statusDetail: PropTypes.func,
};
